import { useCallback } from 'react';
import { trackCustomEvent } from '../utilities/app-insights';
import { getEnrolmentStatus, myobDisconnect, xeroDisconnect } from '../actions/accountingIntegrationActions';

/**
 * Custom hook to manage event handlers for accounting integration
 * Handles connect/disconnect actions with GTM tracking
 *
 * STABILITY FIX: All handlers wrapped in useCallback to prevent unnecessary re-renders
 * of child components that receive these callbacks as props
 */
export const useAccountingIntegrationHandlers = ({
  dispatch,
  xeroConnectButtonDisabled,
  myobConnectButtonDisabled,
  xeroDisconnectLoading,
  myobDisconnectLoading,
  xeroLoginUri,
  myobLoginUri,
  showInProgressView,
  showHomeView,
  setConnectStatusLoading,
  loggedInUser,
  assignedAccountNumber,
  assignedGrandfatherClientNumber,
  accountId,
}) => {
  const handleConnectToXeroClick = useCallback(() => {
    if (xeroConnectButtonDisabled) {
      return;
    }

    window.open(xeroLoginUri, '_blank');
    showInProgressView('Xero');
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Account_Integration_Connect_Xero',
      selected: true,
    });
    trackCustomEvent('Click Connect to Xero', loggedInUser, assignedAccountNumber, 'Clicked Connect to Xero.', {
      selectedAccount: accountId,
    });
  }, [xeroConnectButtonDisabled, xeroLoginUri, showInProgressView, loggedInUser, assignedAccountNumber, accountId]);

  const handleDisconnectFromXeroClick = useCallback(() => {
    if (xeroDisconnectLoading) {
      return;
    }

    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Account_Integration_Disconnect_Xero',
      button_click: true,
    });
    dispatch(xeroDisconnect(assignedGrandfatherClientNumber, accountId));
    trackCustomEvent('Click Disconnect Xero', loggedInUser, assignedAccountNumber, 'Clicked Disconnect from Xero.', {
      selectedAccount: accountId,
    });
  }, [
    xeroDisconnectLoading,
    dispatch,
    assignedGrandfatherClientNumber,
    accountId,
    loggedInUser,
    assignedAccountNumber,
  ]);

  const handleConnectToMyobClick = useCallback(() => {
    if (myobConnectButtonDisabled) {
      return;
    }

    window.open(myobLoginUri, '_blank');
    showInProgressView('MYOB');
    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Account_Integration_Connect_MYOB',
      selected: true,
    });
    trackCustomEvent('Click Connect to MYOB', loggedInUser, assignedAccountNumber, 'Clicked Connect to MYOB.', {
      selectedAccount: accountId,
    });
  }, [myobConnectButtonDisabled, myobLoginUri, showInProgressView, loggedInUser, assignedAccountNumber, accountId]);

  const handleDisconnectFromMyobClick = useCallback(() => {
    if (myobDisconnectLoading) {
      return;
    }

    window.dataLayer.push({
      event: 'Card_Portal_Accounts_Account_Integration_Disconnect_MYOB',
      button_click: true,
    });
    dispatch(myobDisconnect(assignedGrandfatherClientNumber, accountId));
    trackCustomEvent('Click Disconnect MYOB', loggedInUser, assignedAccountNumber, 'Clicked Disconnect from MYOB.', {
      selectedAccount: accountId,
    });
  }, [
    myobDisconnectLoading,
    dispatch,
    assignedGrandfatherClientNumber,
    accountId,
    loggedInUser,
    assignedAccountNumber,
  ]);

  const handleInProgressCompleteClick = useCallback(() => {
    dispatch(getEnrolmentStatus(assignedGrandfatherClientNumber, accountId, true));
    showHomeView();
    setConnectStatusLoading(true);
    trackCustomEvent(
      'Click Done Xero or MYOB',
      loggedInUser,
      assignedAccountNumber,
      'Clicked Done to complete Xero or MYOB connection.',
      { selectedAccount: accountId }
    );
  }, [
    dispatch,
    assignedGrandfatherClientNumber,
    accountId,
    showHomeView,
    setConnectStatusLoading,
    loggedInUser,
    assignedAccountNumber,
  ]);

  return {
    handleConnectToXeroClick,
    handleDisconnectFromXeroClick,
    handleConnectToMyobClick,
    handleDisconnectFromMyobClick,
    handleInProgressCompleteClick,
  };
};
